import React, { useCallback, useLayoutEffect, useRef } from 'react';
import { useDeepCompareMemo } from 'use-deep-compare';
import _uniqBy from 'lodash/uniqBy';
import _cloneDeep from 'lodash/cloneDeep';

import { locale, hasGapAnalysisData } from '@trqk/utils';

import { ReportPeriodColumn } from './components';
import { rowHeight, columnWidth } from './constants';

import { usePaymentPlanStatus } from '../../../../../../../hooks';

interface ReportPeriodSelectorProps {
    context: GapAnalysisContext;
    send: SendGapAnalysisEvent;
    stepId: GapAnalysisStepId;
}

const rowClasses = 'flex flex-col justify-center items-end text-right pr-2';

/**
 * Shows the array of context.periodSelections as a row of <ReportPeriodColumn>s. Each
 * <ReportPeriodColumn> allows the user to select that particular performance period for gap
 * analysis.
 * @param props - {@link ReportPeriodSelectorProps}
 * @returns JSX
 */
export const ReportPeriodSelector = ({
    context,
    send,
    stepId,
}: ReportPeriodSelectorProps): JSX.Element => {
    const { canUploadDetections } = usePaymentPlanStatus();
    const { periodSelections, selectedPeriod, trqkGap } = context;

    const gridContainerRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        if (gridContainerRef.current) {
            gridContainerRef.current.scrollLeft =
                periodSelections.length * columnWidth;
        }
    }, [gridContainerRef.current]);

    const onSelectGapAnalysisPeriod = useCallback(
        ({
            hasDetectionData,
            hasPerformanceData,
            nextSelectedPeriod,
        }: OnSelectGapAnalysisPeriodInput): void => {
            let statusMessage =
                locale.enUS.MANAGE_GAP_ANALYSIS.STEPS.SELECT_PERIOD.NEXT_STEPS
                    .CONFIRM_SELECTION;

            if (canUploadDetections) {
                // User can upload detections; show messaging accordingly
                if (!hasDetectionData && hasPerformanceData) {
                    statusMessage =
                        locale.enUS.MANAGE_GAP_ANALYSIS.STEPS.SELECT_PERIOD
                            .NEXT_STEPS.UPLOAD_DETECTIONS;
                } else if (hasDetectionData && !hasPerformanceData) {
                    statusMessage =
                        locale.enUS.MANAGE_GAP_ANALYSIS.STEPS.SELECT_PERIOD
                            .NEXT_STEPS.UPLOAD_STATEMENT;
                } else if (!hasDetectionData && !hasPerformanceData) {
                    statusMessage =
                        locale.enUS.MANAGE_GAP_ANALYSIS.STEPS.SELECT_PERIOD
                            .NEXT_STEPS.UPLOAD_STATEMENT_AND_DETECTIONS;
                }
            }

            if (!canUploadDetections) {
                // User cannot upload detections; show messaging accordingly
                if (!hasDetectionData) {
                    statusMessage =
                        locale.enUS.MANAGE_GAP_ANALYSIS.STEPS.SELECT_PERIOD
                            .NEXT_STEPS.SELECT_PERIOD_WITH_DETECTIONS;
                } else if (hasDetectionData && !hasPerformanceData) {
                    statusMessage =
                        locale.enUS.MANAGE_GAP_ANALYSIS.STEPS.SELECT_PERIOD
                            .NEXT_STEPS.UPLOAD_STATEMENT;
                }
            }

            const title = nextSelectedPeriod.performance_period_label;

            send({
                type: 'SET_SELECTED_PERIOD',
                value: {
                    selectedPeriod: nextSelectedPeriod,
                },
            });

            if (
                canUploadDetections ||
                (!canUploadDetections && hasDetectionData)
            ) {
                send({
                    type: 'UPDATE_STEP',
                    value: {
                        stepId,
                        update: {
                            disableNextStep: false,
                            statusMessage,
                            title,
                        },
                    },
                });
            } else {
                send({
                    type: 'UPDATE_STEP',
                    value: {
                        stepId,
                        update: {
                            disableNextStep: true,
                            statusMessage,
                            title,
                        },
                    },
                });
            }
        },
        [canUploadDetections],
    );

    const uniquePeriodSelections = useDeepCompareMemo(() => {
        const sorted = _cloneDeep(periodSelections).sort((a, b) => {
            const orderA = a?.order ?? 0;
            const orderB = b?.order ?? 0;

            if (orderA > orderB) {
                return 1;
            }

            if (orderB > orderA) {
                return -1;
            }

            return 0;
        });

        // The API sometimes returns more than one periodSelection for the same period, so we need
        // to de-duplicate the list. This is a safeguard until the issue is fixed in the backend.
        return _uniqBy(
            sorted,
            periodSelection =>
                periodSelection?.performance_period_label ?? null,
        );
    }, [periodSelections]);

    return (
        <div className="text-sm font-semibold">
            <div
                className="grid"
                style={{ gridTemplateColumns: `150px ${columnWidth * 8}px` }}
            >
                <div className="px-4" style={{ gridArea: '1 / 2 / 1 / 2' }}>
                    <div className="flex flex-col items-center w-full mb-2">
                        <p className="text-sm font-semibold">
                            {
                                locale.enUS.MANAGE_GAP_ANALYSIS
                                    .REPORT_PERIOD_SELECTOR.PERFORMANCE_PERIODS
                            }
                        </p>
                        <div
                            className="w-full border-t border-l border-r rounded-tl-lg rounded-tr-lg border-tertiary-300"
                            style={{
                                height: 10,
                            }}
                        />
                    </div>
                </div>
                <div
                    className="border border-transparent"
                    style={{ gridArea: '2 / 1 / 2 / 1' }}
                >
                    <p className={rowClasses} style={{ height: rowHeight }}>
                        {
                            locale.enUS.MANAGE_GAP_ANALYSIS
                                .REPORT_PERIOD_SELECTOR.PERIOD
                        }
                    </p>
                    <p className={rowClasses} style={{ height: rowHeight }}>
                        {
                            locale.enUS.MANAGE_GAP_ANALYSIS
                                .REPORT_PERIOD_SELECTOR.PERFORMANCES
                        }
                    </p>
                    <p className={rowClasses} style={{ height: rowHeight }}>
                        {
                            locale.enUS.MANAGE_GAP_ANALYSIS
                                .REPORT_PERIOD_SELECTOR.DETECTIONS
                        }
                    </p>
                    <p className={rowClasses} style={{ height: rowHeight }}>
                        {
                            locale.enUS.MANAGE_GAP_ANALYSIS
                                .REPORT_PERIOD_SELECTOR.SELECT_PERIOD
                        }
                    </p>
                </div>
                <div
                    className="flex overflow-x-auto bg-tertiary-100"
                    // Using `direction: 'rtl'` here and .sort() below to make the time periods
                    // scroll starting from the right, with most recent at the right
                    style={{ gridArea: '2 / 2 / 2 / 2', direction: 'rtl' }}
                    ref={gridContainerRef}
                >
                    {uniquePeriodSelections.map(
                        (
                            periodSelection: GapAnalysisPeriodSelection,
                        ): JSX.Element | null => {
                            const label =
                                periodSelection?.performance_period_label ??
                                null;

                            if (label === null) {
                                return null;
                            }

                            const { hasDetectionData, hasPerformanceData } =
                                hasGapAnalysisData(periodSelection, trqkGap);

                            return (
                                <ReportPeriodColumn
                                    key={label}
                                    hasDetectionData={hasDetectionData}
                                    hasPerformanceData={hasPerformanceData}
                                    label={label}
                                    onSelectGapAnalysisPeriod={
                                        onSelectGapAnalysisPeriod
                                    }
                                    periodSelection={periodSelection}
                                    selectedPeriod={selectedPeriod}
                                />
                            );
                        },
                    )}
                </div>
            </div>
        </div>
    );
};
